import { getTestVariant } from '@bridebook/toolbox/src/ab-testing/lib/selectors';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { useABTestIntegration } from '@bridebook/web/lib/ab-tests/hooks/use-ab-test-integration';
import { IApplicationState } from '@bridebook/web/lib/types';
import { useSelector } from '@bridebook/web/lib/utils';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { useSupplierPricingGroupedByYear } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import { hasPricing } from '@bridebook/toolbox/src/supplier/utils/pricing';
import { resolveSupplierBucketSetting } from '@bridebook/toolbox/src/gazetteer/resolve-supplier-bucket-setting';
import type { ISupplier_Type } from '@bridebook/models/source/models/Suppliers.types';
import { useMemo } from 'react';

const testId = 'LIVE-25246_PriceEstimate';

/**
 * Returns the active variant of the test for use in selectors, components etc.
 * If the test is not activated, it returns 'control'
 */
export const getVariant_PriceEstimate = (state: IApplicationState) => getTestVariant(state, testId);

/**
 * Hook for initializing the test
 */
export const useABTest_PriceEstimate = () => {
  const market = useMarket();
  const triggerCondition = market.country === CountryCodes.GB;
  useABTestIntegration({ testId, triggerCondition });
};

export const usePriceEstimateToolBasicData = ({
  supplierId,
  supplierType,
  supplierCountry,
}: {
  supplierId?: string;
  supplierType?: ISupplier_Type;
  supplierCountry?: CountryCodes;
}) => {
  const LIVE_25246_Variant = useSelector(getVariant_PriceEstimate);
  const disable = LIVE_25246_Variant === 'control';

  // Whether the supplier takes part at all, settled before any pricing is read.
  const { isEnabled: isSmartPricingEnabled } = useMemo(() => {
    const market = supplierCountry ? gazetteer.getMarketByCountry(supplierCountry) : undefined;

    return resolveSupplierBucketSetting('smartPricing', supplierType, market);
  }, [supplierCountry, supplierType]);

  // Fetched for both variants, so analytics reports the same thing either way.
  const { data: supplierPricingGroupedByYear, isLoading } = useSupplierPricingGroupedByYear(
    { supplierId },
    { disable: !isSmartPricingEnabled },
  );

  // Packages repeat across years, which the presence check doesn't care about.
  const prices = Object.values(supplierPricingGroupedByYear ?? {}).flat();

  return {
    isEnabled: !disable && isSmartPricingEnabled && hasPricing(prices),
    isLoading: isSmartPricingEnabled && isLoading,
  };
};
